import React from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, 
  Car, 
  Sparkles, 
  Shield, 
  Wrench, 
  Settings, 
  Clock, 
  CheckCircle, 
  Star, 
  Phone, 
  Mail 
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import LoadingSpinner, { LoadingCard } from '../components/LoadingSpinner';
import { ErrorCard } from '../components/ErrorMessage';
import { useAPI } from '../hooks/useAPI';
import { servicesAPI, pricingAPI } from '../services/api';

const ServicesAndPricingPage = () => {
  // API calls pour services et tarifs
  const { 
    data: services, 
    loading: servicesLoading, 
    error: servicesError,
    refetch: refetchServices 
  } = useAPI(() => servicesAPI.getAll());

  const { 
    data: allPricing, 
    loading: pricingLoading, 
    error: pricingError,
    refetch: refetchPricing 
  } = useAPI(() => pricingAPI.getAll());

  // Organiser les tarifs par catégorie
  const simpleServices = allPricing?.filter(p => p.category === 'simple')?.sort((a, b) => (a.order || 0) - (b.order || 0)) || [];
  const principalServices = allPricing?.filter(p => p.category === 'principal')?.sort((a, b) => (a.order || 0) - (b.order || 0)) || [];
  const specialServices = allPricing?.filter(p => p.category === 'special')?.sort((a, b) => (a.order || 0) - (b.order || 0)) || [];

  const getIcon = (iconName) => {
    const icons = {
      'Car': Car,
      'Sparkles': Sparkles,
      'Shield': Shield,
      'Wrench': Wrench,
      'Settings': Settings
    };
    return icons[iconName] || Car;
  };

  const ServiceCard = ({ service, featured = false }) => {
    const isDevis = service.price === 'Devis personnalisé';
    
    return (
      <Card className={`h-full group hover:shadow-xl transition-all duration-300 ${featured ? 'ring-2 ring-blue-600 scale-105' : ''}`}>
        <CardHeader className="relative">
          {featured && (
            <Badge className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-blue-600 text-white">
              Plus populaire
            </Badge>
          )}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Clock className="w-5 h-5 text-blue-600" />
              <span className="text-sm text-gray-600">{service.duration}</span>
            </div>
            {featured && <Star className="w-5 h-5 text-yellow-400 fill-current" />}
          </div>
          <CardTitle className="text-xl text-gray-900">{service.service}</CardTitle>
          <div className="flex items-baseline space-x-1">
            <span className={`text-3xl font-bold ${isDevis ? 'text-orange-600' : 'text-blue-600'}`}>
              {service.price}
            </span>
            {service.price.includes('partir') && <span className="text-sm text-gray-500">*</span>}
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <ul className="space-y-3 mb-6">
            {service.includes.map((item, index) => (
              <li key={index} className="flex items-start space-x-3">
                <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-gray-600">{item}</span>
              </li>
            ))}
          </ul>
          
          {isDevis ? (
            <div className="space-y-2">
              <Link to="/contact?sujet=devis">
                <Button className="w-full bg-orange-600 hover:bg-orange-700 text-white group-hover:scale-105 transition-transform duration-200">
                  <Mail className="w-4 h-4 mr-2" />
                  Demander un devis
                </Button>
              </Link>
              <a href="tel:0622360190">
                <Button variant="outline" className="w-full text-orange-600 border-orange-600 hover:bg-orange-50">
                  <Phone className="w-4 h-4 mr-2" />
                  06 22 36 01 90
                </Button>
              </a>
            </div>
          ) : (
            <Link to={`/reservation?service=${encodeURIComponent(service.service)}`}>
              <Button className={`w-full ${featured ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-900 hover:bg-gray-800'} text-white group-hover:scale-105 transition-transform duration-200`}>
                Réserver ce service
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          )}
        </CardContent>
      </Card>
    );
  };

  const loading = servicesLoading || pricingLoading;
  const error = servicesError || pricingError;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Hero Section Skeleton */}
        <section className="bg-gradient-to-br from-blue-600 to-blue-800 text-white py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-4xl mx-auto">
              <div className="h-12 bg-blue-500 rounded mb-6 animate-pulse"></div>
              <div className="h-6 bg-blue-500 rounded mb-8 animate-pulse"></div>
            </div>
          </div>
        </section>
        
        {/* Loading Cards */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[...Array(6)].map((_, index) => (
                <LoadingCard key={index} />
              ))}
            </div>
          </div>
        </section>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <ErrorCard error={error} onRetry={() => {
          refetchServices();
          refetchPricing();
        }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-600 to-blue-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Nos Tarifs AutoSpa 77
            </h1>
            <p className="text-xl text-blue-100 mb-8">
              Des formules transparentes et des services personnalisés 
              pour tous vos besoins de detailing automobile.
            </p>
            <div className="flex items-center justify-center space-x-8 text-sm">
              <div className="flex items-center space-x-2">
                <CheckCircle className="w-5 h-5" />
                <span>Produits professionnels</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle className="w-5 h-5" />
                <span>Prix transparents</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle className="w-5 h-5" />
                <span>Garantie satisfait</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle className="w-5 h-5" />
                <span>10+ ans d'expérience</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section supprimée comme demandé */}

      {/* Lavages Simples - nouveaux */}
      {simpleServices.length > 0 && (
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Lavages Simples
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Des prestations ciblées intérieur ou extérieur à prix accessibles
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {simpleServices.map((service) => (
                <ServiceCard key={service.id} service={service} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Formules Principales - LAVAGE renommé */}
      {principalServices.length > 0 && (
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Nos Formules Principales
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Deux formules complètes pour répondre à tous vos besoins de nettoyage
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {principalServices.map((service, index) => (
                <ServiceCard 
                  key={service.id} 
                  service={service} 
                  featured={index === 1} // LAVAGE 2 mis en avant
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Services Detailing Personnalisé */}
      {specialServices.length > 0 && (
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Detailing Personnalisé
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Services à la carte pour une prestation sur-mesure
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {specialServices.map((service) => (
                <ServiceCard key={service.id} service={service} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Informations supplémentaires */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Pourquoi choisir AutoSpa 77 ?
              </h2>
              <ul className="space-y-4">
                <li className="flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 text-green-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <strong className="text-gray-900">Expertise professionnelle :</strong>
                    <span className="text-gray-600 ml-2">Plus de 10 ans d'expérience dans le detailing automobile</span>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 text-green-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <strong className="text-gray-900">Matériel haut de gamme :</strong>
                    <span className="text-gray-600 ml-2">Équipements professionnels et produits de qualité supérieure</span>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 text-green-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <strong className="text-gray-900">Satisfaction garantie :</strong>
                    <span className="text-gray-600 ml-2">Service refait gratuitement si vous n'êtes pas satisfait</span>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 text-green-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <strong className="text-gray-900">Deux adresses :</strong>
                    <span className="text-gray-600 ml-2">Ozoir-la-Ferrière et Croissy-Beaubourg pour vous servir</span>
                  </div>
                </li>
              </ul>
            </div>
            
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Devis personnalisé gratuit
                </h3>
                <p className="text-gray-600 mb-6">
                  Chaque véhicule est unique. Contactez-nous pour un devis sur mesure 
                  adapté à vos besoins spécifiques.
                </p>
                <div className="space-y-3">
                  <a href="tel:0622360190">
                    <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white">
                      <Phone className="w-5 h-5 mr-2" />
                      Appelez-nous : 06 22 36 01 90
                    </Button>
                  </a>
                  <Link to="/contact?sujet=devis">
                    <Button variant="outline" className="w-full border-blue-600 text-blue-600 hover:bg-blue-50">
                      Demander un devis en ligne
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Notes importantes */}
      <section className="py-12 bg-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Notes importantes</h3>
            <div className="text-sm text-gray-600 space-y-2 max-w-4xl mx-auto">
              <p>* Les durées d'intervention sont estimatives et peuvent varier selon l'état du véhicule.</p>
              <p>• Tous les prix incluent les produits professionnels et la main-d'œuvre qualifiée.</p>
              <p>• Devis gratuit et sans engagement sur simple demande pour les services personnalisés.</p>
              <p>• Les tarifs indiqués sont HT (Hors taxes) sauf mention contraire.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Prêt à redonner vie à votre véhicule ?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Réservez dès maintenant ou contactez-nous pour un devis personnalisé
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/reservation">
              <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-4 text-lg">
                Réserver LAVAGE 1 ou 2
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/contact?sujet=devis">
              <Button variant="outline" size="lg" className="border-white text-white hover:bg-white hover:text-blue-600 px-8 py-4 text-lg">
                Demander un devis
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesAndPricingPage;